import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useBranch } from './BranchContext';
import { getStaffLocations } from '../services/api';
import { getLogoUrlFromMedia } from '../constants';

const OwnerViewContext = createContext(null);

const SUPER_ROLES = ['Restaurant_Owner', 'Regional_Employee', 'Magil_Admin'];

// Module-level cache so SnapshotScreen (outside OwnerViewProvider) can read branch count
// without navigating to /owner-view first. Null = not yet determined.
let _ownerLocationCount: number | null = null;
export const getOwnerLocationCount = () => _ownerLocationCount;
export const setOwnerLocationCount = (count: number | null) => { _ownerLocationCount = count; };

/**
 * Derives brand list from location names.
 * Each location label is "BrandName, StoreName" — extract first segment, dedupe.
 */
const deriveBrands = (locations: any[]) => {
    const seen = new Set();
    const brands = [];
    for (const loc of locations) {
        const name = loc.locationName ?? loc.name ?? loc.label ?? '';
        const brand = name.includes(',') ? name.split(',')[0].trim() : name.trim();
        if (brand && !seen.has(brand.toLowerCase())) {
            seen.add(brand.toLowerCase());
            brands.push({ label: brand, value: brand });
        }
    }
    return brands;
};

/**
 * Filters locations whose name starts with the selected brand.
 */
const filterByBrand = (locations: any[], brand: any) => {
    if (!brand || !brand.value) return locations;
    return locations.filter((loc) => {
        const name = loc.locationName ?? loc.name ?? loc.label ?? '';
        return name.toLowerCase().startsWith(brand.value.toLowerCase());
    });
};

const sameLocation = (a: any, b: any): boolean => {
    if (!a || !b) return false;
    const idA = a.id ?? a.locationId;
    const idB = b.id ?? b.locationId;
    if (idA == null || idB == null) return false;
    return String(idA) === String(idB);
};

export const OwnerViewProvider = ({ children }: { children: React.ReactNode }) => {
    const { auth } = useAuth();
    const { selectedBranch, setSelectedBranch } = useBranch();

    const role = auth?.role ?? '';
    const isSuperUser = SUPER_ROLES.includes(role);
    const token = auth?.accessToken ?? auth?.access_token ?? null;
    const staffId = auth?.id ?? '';

    // All locations for this merchant
    const [allLocations, setAllLocations] = useState<any[]>([]);
    // Start as true so we don't redirect before the first fetch completes
    const [locationsLoading, setLocationsLoading] = useState(true);

    // Brand / store selection
    const [selectedBrand, setSelectedBrand] = useState<any>(null); // { label, value }
    const [selectedStore, setSelectedStore] = useState<any>(null);  // location object or "all" option

    const isSingleBranch = !locationsLoading && allLocations.length === 1;

    // Fetch all locations on mount — uses /staff/{id}/locations (same as maghil-portal)
    useEffect(() => {
        if (!staffId || !token) return;
        setLocationsLoading(true);
        getStaffLocations(staffId, token)
            .then((res) => {
                const payload = res?.metaDataInfo?.data ?? res?.data ?? res;
                const list = Array.isArray(payload) ? payload : payload?.branch ?? payload?.branches ?? [];
                const normalised = list.map((item) => ({
                    ...item,
                    locationId: item.id ?? item.locationId,
                    locationName: item.name ?? item.locationName ?? item.label ?? '',
                }));
                _ownerLocationCount = normalised.length;
                setAllLocations(normalised);
            })
            .catch(() => setAllLocations([]))
            .finally(() => setLocationsLoading(false));
    }, [staffId, token]);

    // Derived brand list
    const brands = useMemo(() => {
        const derived = deriveBrands(allLocations);
        return [{ label: 'All brands', value: '' }, ...derived];
    }, [allLocations]);

    // Filtered stores by brand
    const filteredStores = useMemo(() => {
        const filtered = filterByBrand(allLocations, selectedBrand);
        // Build "All stores" option with comma-separated IDs
        if (filtered.length > 1) {
            const allIds = filtered.map((l) => l.locationId ?? l.id).filter(Boolean).join(',');
            return [
                { id: allIds, locationName: 'All stores', isAll: true },
                ...filtered,
            ];
        }
        return filtered;
    }, [allLocations, selectedBrand]);

    // When brand changes, reset store to first option
    useEffect(() => {
        if (filteredStores.length === 0) {
            setSelectedStore(null);
            return;
        }
        setSelectedStore((prev) => {
            if (prev && filteredStores.some((s) => sameLocation(s, prev))) {
                return prev;
            }
            const allOption = filteredStores.find((s) => s.isAll);
            if (allOption) return allOption;
            const bid = selectedBranch?.id ?? selectedBranch?.locationId;
            if (bid) {
                const fromBranch = filteredStores.find((s) => !s.isAll && sameLocation(s, { id: bid, locationId: bid }));
                if (fromBranch) return fromBranch;
            }
            return filteredStores[0];
        });
    }, [selectedBrand, filteredStores, selectedBranch]);

    // Initialize brand on location load
    useEffect(() => {
        if (brands.length > 1 && !selectedBrand) {
            setSelectedBrand(brands[0]); // "All brands"
        }
    }, [brands.length]);

    const changeBrand = useCallback((brand: any) => {
        setSelectedBrand(brand);
        // "All brands" selected — clear store so the effect falls through to the "All stores" option
        if (!brand?.value) {
            setSelectedStore(null);
        }
    }, []);

    // Pure store-filter setter for the Owner Dashboard. Updates only the dashboard's
    // own selection — it deliberately does NOT touch the global selectedBranch, so the
    // sidebar outlet switcher stays independent of the dashboard dropdowns.
    const changeStore = useCallback((store: any) => {
        setSelectedStore(store);
    }, []);

    // Selects a store AND promotes it to the global branch. Use when drilling into a
    // single outlet's reports (e.g. mobile outlet-card tap → snapshot), where the
    // sidebar/global branch is expected to follow the chosen outlet.
    const openStoreAsBranch = useCallback((store: any) => {
        setSelectedStore(store);
        if (store && !store.isAll) {
            const id = store.id ?? store.locationId;
            const name = store.locationName ?? store.name ?? 'Branch';
            const logoUrl = getLogoUrlFromMedia(store?.media) || store?.logoUrl || '';
            setSelectedBranch({ ...store, id: store.id ?? id, locationName: store.locationName ?? store.name ?? name, logoUrl });
        }
    }, [setSelectedBranch]);

    const value = useMemo(() => ({
        isSuperUser,
        isSingleBranch,
        allLocations,
        locationsLoading,
        brands,
        selectedBrand,
        changeBrand,
        filteredStores,
        selectedStore,
        changeStore,
        openStoreAsBranch,
    }), [
        isSuperUser, isSingleBranch, allLocations, locationsLoading,
        brands, selectedBrand, changeBrand,
        filteredStores, selectedStore, changeStore, openStoreAsBranch,
    ]);

    return (
        <OwnerViewContext.Provider value={value}>
            {children}
        </OwnerViewContext.Provider>
    );
};

export const useOwnerView = () => {
    const ctx = useContext(OwnerViewContext);
    if (!ctx) throw new Error('useOwnerView must be used within OwnerViewProvider');
    return ctx;
};

export const useOwnerViewOptional = () => useContext(OwnerViewContext);
